import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AmaFeatureDemo from './AmaFeatureDemo';
import './AmaLaunchModal.css';

interface AmaLaunchModalProps {
  open: boolean;
  enabled: boolean;
  saving: boolean;
  accentColor?: string;
  gradientStart?: string;
  gradientEnd?: string;
  creatorSlug?: string;
  onToggle: (enabled: boolean) => Promise<boolean>;
  onTry: () => Promise<boolean>;
  onClose: () => void;
}

const AmaLaunchModal: React.FC<AmaLaunchModalProps> = ({
  open,
  enabled,
  saving,
  accentColor = '#8b5cf6',
  gradientStart = '#4c1d95',
  gradientEnd = '#db2777',
  creatorSlug,
  onToggle,
  onTry,
  onClose,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onClose);
  const busyRef = useRef(false);
  const [trying, setTrying] = useState(false);
  const busy = saving || trying;

  useEffect(() => {
    closeRef.current = onClose;
    busyRef.current = busy;
  }, [busy, onClose]);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const focusTimer = window.setTimeout(() => {
      dialogRef.current?.querySelector<HTMLElement>('[data-ama-launch-primary]')?.focus();
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busyRef.current) {
        event.preventDefault();
        closeRef.current();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      ));
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const handleTry = async () => {
    if (busy) return;
    setTrying(true);
    try {
      await onTry();
    } finally {
      setTrying(false);
    }
  };

  const modalStyle = {
    '--ama-accent': accentColor,
    '--ama-gradient-start': gradientStart,
    '--ama-gradient-end': gradientEnd,
  } as React.CSSProperties;

  return createPortal(
    <div
      className="ama-launch-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="ama-launch-dialog"
        style={modalStyle}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ama-launch-title"
        aria-describedby="ama-launch-description"
      >
        <div className="ama-launch-glow ama-launch-glow-one" />
        <div className="ama-launch-glow ama-launch-glow-two" />

        <div className="ama-launch-content">
          <div className="ama-launch-heading-row">
            <div className="ama-launch-eyebrow">
              <span className="ama-launch-eyebrow-dot" />
              New · Ask Me Anything
            </div>
            <button
              type="button"
              className="ama-launch-close"
              onClick={onClose}
              disabled={busy}
              aria-label="Close Ask Me Anything announcement"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                <path strokeLinecap="round" d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
          </div>

          <div className="ama-launch-copy">
            <h2 id="ama-launch-title">Turn questions into more views</h2>
            <p id="ama-launch-description">
              <span className="ama-launch-description-desktop">
                Fans ask anonymously on your .fans page. You answer on Instagram Stories,
                giving followers a reason to visit your link. More page views create more
                chances for sponsor ads to be seen and clicked—giving you more opportunities to earn.
              </span>
              <span className="ama-launch-description-mobile">
                Answer anonymous fan questions on Instagram Stories and bring more people back to your page.
              </span>
            </p>
          </div>

          <div className="ama-launch-revenue-strip">
            <span className="ama-launch-revenue-icon" aria-hidden="true">↗</span>
            <span>More conversation</span>
            <span aria-hidden="true">→</span>
            <span>more visits</span>
            <span aria-hidden="true">→</span>
            <strong>more earning opportunities</strong>
          </div>

          <div className="ama-launch-mobile-revenue">
            <span className="ama-launch-revenue-icon" aria-hidden="true">↗</span>
            <span><strong>More page views</strong> means more chances for sponsor ads to earn.</span>
          </div>

          <AmaFeatureDemo
            creatorSlug={creatorSlug}
            accentColor={accentColor}
            gradientStart={gradientStart}
            gradientEnd={gradientEnd}
          />

          <div className="ama-launch-flow" role="group" aria-label="How Ask Me Anything works">
            <span>Fans ask anonymously</span>
            <b aria-hidden="true">→</b>
            <span>You tap Answer</span>
            <b aria-hidden="true">→</b>
            <span>Post to your Story</span>
          </div>

          <div className="ama-launch-switch-card">
            <div>
              <strong>Turn on Ask Me Anything</strong>
              <p>You can turn it off anytime in Design.</p>
            </div>
            <label className="ama-launch-switch">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(event) => { void onToggle(event.target.checked); }}
                disabled={busy}
                aria-label="Turn on Ask Me Anything"
              />
              <span aria-hidden="true" />
            </label>
          </div>

          <div className="ama-launch-actions">
            <button type="button" className="ama-launch-later" onClick={onClose} disabled={busy}>
              Not now
            </button>
            <button
              type="button"
              className="ama-launch-primary"
              onClick={handleTry}
              disabled={busy}
              data-ama-launch-primary
            >
              {trying ? (
                <><span className="ama-launch-spinner" /> Opening AMA…</>
              ) : saving ? (
                <><span className="ama-launch-spinner" /> Saving…</>
              ) : (
                <>Try AMA <span aria-hidden="true">→</span></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AmaLaunchModal;
